import React from "react";
import {
  Box,
  Stack,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  Typography,
} from "@mui/material";
import { Delete, Add, Link as LinkIcon } from "@mui/icons-material";

export const SOCIAL_PLATFORM_OPTIONS = [
  "Facebook",
  "Twitter",
  "Instagram",
  "YouTube",
  "Other",
];

const isValidURL = (url) => {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

export default function SocialLinkSection({
  links = [],
  onChange,
  onAdd,
  onRemove,
  saving = false,
}) {
  const draftRow = links.length > 0 ? links[links.length - 1] : null;
  const isDraftValid =
    draftRow &&
    !draftRow.id &&
    draftRow.url &&
    isValidURL(draftRow.url) &&
    (draftRow.platform !== "Other" ||
      (draftRow.customPlatform && draftRow.customPlatform.trim() !== ""));

  const handlePlatformChange = (idx, value) => {
    if (links[idx]?.id) return;
    onChange(idx, "platform", value);
    if (value !== "Other") onChange(idx, "customPlatform", "");
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardHeader
        title="Rate Us / Follow Us"
        subheader="Add your social or review profile links. Saved links are included in feedback requests."
        titleTypographyProps={{ variant: "h3", fontWeight: 700 }}
      />
      <Divider />

      <CardContent>
        <Stack spacing={2}>
          {links.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No links yet. Use the form below to add one.
            </Typography>
          )}

          {links.map((item, idx) => {
            const isSaved = Boolean(item.id);
            const urlValid = !item.url || isValidURL(item.url);
            const isOther = item.platform === "Other";

            return (
              <Box
                key={item.id || `draft-${idx}`}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isSaved ? "divider" : "primary.light",
                  bgcolor: isSaved ? "action.hover" : "background.paper",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                <FormControl
                  size="small"
                  sx={{ minWidth: 180, flex: { xs: "1 1 100%", md: "0 0 180px" } }}
                  disabled={isSaved || saving}
                >
                  <InputLabel>Platform</InputLabel>
                  <Select
                    label="Platform"
                    value={item.platform ?? "Facebook"}
                    onChange={(e) => handlePlatformChange(idx, e.target.value)}
                  >
                    {SOCIAL_PLATFORM_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {isOther && (
                  <TextField
                    size="small"
                    label="Custom Platform"
                    placeholder="e.g., Tripadvisor"
                    value={item.customPlatform || ""}
                    onChange={(e) =>
                      onChange(idx, "customPlatform", e.target.value)
                    }
                    disabled={isSaved || saving}
                    sx={{ minWidth: 180, flex: { xs: "1 1 100%", md: "0 0 180px" } }}
                  />
                )}

                <TextField
                  size="small"
                  label="Profile/Page Link"
                  placeholder="https://example.com/your-page"
                  value={item.url}
                  onChange={(e) => onChange(idx, "url", e.target.value.trim())}
                  disabled={isSaved || saving}
                  error={Boolean(item.url) && !urlValid}
                  helperText={
                    item.url && !urlValid
                      ? "Enter a valid URL (http:// or https://)"
                      : null
                  }
                  InputProps={
                    isSaved
                      ? {
                          startAdornment: (
                            <LinkIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                          ),
                        }
                      : undefined
                  }
                  sx={{ flex: "1 1 280px", minWidth: 200 }}
                />

                <Tooltip title="Remove this link">
                  <span>
                    <IconButton
                      color="error"
                      onClick={() => onRemove(idx)}
                      disabled={saving}
                    >
                      <Delete />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}

          <Box display="flex" justifyContent="flex-end">
            <Tooltip
              title={
                draftRow && !draftRow.id && !isDraftValid
                  ? "Enter platform and a valid URL to save this link"
                  : "Save link and add another"
              }
            >
              <span>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  onClick={onAdd}
                  disabled={saving || (Boolean(draftRow) && !draftRow.id && !isDraftValid)}
                >
                  {saving ? "Saving..." : "Add Social Link"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
